import React, { useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { FieldOwnershipBadge } from "@/components/common/FieldOwnershipBadge";
import { Button } from "@/components/ui/button";
import { GeoPoint } from "@/types/campLocation";
import { useAuth } from "@/context/AuthContext";
import { parseGoogleMapsAddress } from "@/utils/addressParser";
import { getCampOrganization } from "@/services/campOrganizationService";
import { db } from "@/services/firebaseService";
import { toast } from "sonner";

export const AddressSection = ({
  form,
  location,
  isWelcomeScreen = false
}: {
  form: any;
  location?: any;
  isWelcomeScreen?: boolean;
}) => {
  const { userProfile } = useAuth();
  const [addressInput, setAddressInput] = useState("");
  const [isLoadingOrgAddress, setIsLoadingOrgAddress] = useState(false);
  
  // Check if user is admin
  const isAdmin = userProfile?.role === 'admin_user';

  // Get latLng value and convert it to a string if it's a GeoPoint
  const latLngValue = form.getValues("address.latLng");
  let latLngString = "";
  
  if (latLngValue) {
    if (typeof latLngValue === 'string') {
      latLngString = latLngValue;
    } else if (latLngValue instanceof GeoPoint) {
      // Convert GeoPoint to string format for the input field
      latLngString = `${latLngValue.latitude}, ${latLngValue.longitude}`;
      // Update the form value to use the string representation for editing
      form.setValue("address.latLng", latLngString);
    }
  }

  const handleAddressInputChange = (value: string) => {
    setAddressInput(value);
    
    if (value.trim()) {
      // Parse the address and update form fields
      const parsed = parseGoogleMapsAddress(value);
      
      // Update form fields with parsed values
      form.setValue("address.address1", parsed.address1);
      form.setValue("address.address2", parsed.address2);
      form.setValue("address.city", parsed.city);
      form.setValue("address.state", parsed.state);
      form.setValue("address.zip", parsed.zip);
      form.setValue("address.country", parsed.country);
    }
  };

  const handleCopyFromOrganization = async () => {
    if (!location?.providedByOrg) {
      toast.error("No organization associated with this location");
      return;
    }

    try {
      setIsLoadingOrgAddress(true);
      
      // Extract organization ID
      let orgId: string;
      if (typeof location.providedByOrg === 'string') {
        orgId = location.providedByOrg;
      } else if (typeof location.providedByOrg === 'object' && 'id' in location.providedByOrg) {
        orgId = location.providedByOrg.id;
      } else {
        toast.error("Invalid organization reference");
        return;
      }

      // Fetch organization data
      const organization = await getCampOrganization(db, orgId);
      
      if (!organization) {
        toast.error("Organization not found");
        return;
      }

      if (!organization.mailingAddress) {
        toast.error("Organization has no mailing address");
        return;
      }

      // Copy address fields from organization to location
      const orgAddress = organization.mailingAddress;
      form.setValue("address.address1", orgAddress.address1 || "");
      form.setValue("address.address2", orgAddress.address2 || "");
      form.setValue("address.city", orgAddress.city || "");
      form.setValue("address.state", orgAddress.state || "");
      form.setValue("address.zip", orgAddress.zip || "");
      form.setValue("address.country", orgAddress.country || "USA");

      toast.success("Address copied from organization");
    } catch (error) {
      console.error("Error copying address from organization:", error);
      toast.error("Failed to copy address from organization");
    } finally {
      setIsLoadingOrgAddress(false);
    }
  };

  // If arriving from kickstart flow, show toast and scroll to this section
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('kickstart') === '1') {
        toast.success("Location created. You can now edit the mailing address.");
        const attemptScroll = () => {
          const el = document.getElementById('mailing-address');
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } else {
            setTimeout(attemptScroll, 100);
          }
        };
        // Schedule scroll after render paint
        setTimeout(attemptScroll, 0);
        // Remove the query param so it doesn't trigger again
        const url = new URL(window.location.href);
        url.searchParams.delete('kickstart');
        window.history.replaceState({}, '', url.toString());
      }
    } catch {
      // intentionally no-op: best-effort URL param handling
      void 0;
    }
  }, []);

  return <Card>
    <CardHeader id="mailing-address">
      <div className="flex items-center gap-2">
        <CardTitle className="text-lg">Address</CardTitle>
        <FieldOwnershipBadge fieldPath="/address" />
      </div>
    </CardHeader>
    <CardContent className="space-y-4">
      {/* Admin-only Google Maps address parser */}
      {isAdmin && !isWelcomeScreen && (
        <FormItem className="flex flex-col items-start">
          <FormLabel className="text-left w-full">Address copied from Google Maps</FormLabel>
          <FormControl>
            <Input 
              value={addressInput}
              onChange={(e) => handleAddressInputChange(e.target.value)}
              placeholder="Paste address from Google Maps (e.g., 2912B Jones Ferry Rd, Chapel Hill, NC 27516)"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}

      {/* Admin-only Copy from Organization button */}
      {isAdmin && !isWelcomeScreen && location?.providedByOrg && (
        <div className="flex justify-start">
          <Button 
            type="button"
            variant="outline" 
            onClick={handleCopyFromOrganization}
            disabled={isLoadingOrgAddress}
            className="mb-2"
          >
            {isLoadingOrgAddress ? "Copying..." : "Copy address from organization"}
          </Button>
        </div>
      )}

      <FormField control={form.control} name="address.address1" render={({
      field
    }: any) => <FormItem>
            <FormLabel className="text-left block">Address Line 1</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />
      <FormField control={form.control} name="address.address2" render={({
      field
    }: any) => <FormItem>
            <FormLabel className="text-left block">Address Line 2</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FormField control={form.control} name="address.city" render={({
        field
      }: any) => <FormItem>
              <FormLabel className="text-left block">City</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={form.control} name="address.state" render={({
        field
      }: any) => <FormItem>
              <FormLabel className="text-left block">State</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={form.control} name="address.zip" render={({
        field
      }: any) => <FormItem>
              <FormLabel className="text-left block">Zip Code</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>
      <FormField control={form.control} name="address.country" render={({
      field
    }: any) => <FormItem>
            <FormLabel className="text-left block">Country</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />
      <FormField 
        control={form.control} 
        name="address.latLng" 
        render={({ field }: any) => (
          <FormItem>
            <FormLabel className="text-left block">Latitude, Longitude</FormLabel>
            <FormControl>
              <Input {...field} placeholder="e.g., 35.915432, -79.090951" disabled={isWelcomeScreen} />
            </FormControl>
            <FormDescription>
              {isWelcomeScreen ? "Contact support@kidsbooked.com to change this field." : "Copy and paste from Google Maps"}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField control={form.control} name="address.webUrl" render={({
      field
    }: any) => <FormItem>
            <FormLabel className="text-left block">Google Maps URL</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />
      <FormField control={form.control} name="address.localityLabel" render={({
      field
    }: any) => <FormItem>
            <FormLabel className="text-left block">Locality Label</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormDescription>Will appear as "Located in [locality]"</FormDescription>
            <FormMessage />
          </FormItem>} />
    </CardContent>
  </Card>;
};
